<i18n>
{
	"en": {
		"admin": "Administrator",
		"updatedCode:": "Code was updated",
		"ban": "Ban user",
		"delete": "Delete user | Delete user | Delete users",
		"editedDate": "Date edited",
		"email": "E-mail address",
		"lastLogin": "Last login",
		"name": "Name",
		"new": "New user",
		"update": "Update",
		"usersFound": "No users found | {count} user found | {count} users found",
		"failedToFetch": "Failed to fetch user data",
		"failedToUpdateName": "Failed to update name",
		"failedToUpdateEmail": "Failed to update e-mail",
		"failedToUpdateSuper": "Failed to update administrator access",
		"failedToDelete": "Could not delete user",
		"failedToBan": "Could not ban user",
		"updatedName": "Successfully updated name",
		"updatedEmail": "Successfully updated e-mail",
		"superPromoted": "Promoted user to administrator",
		"superDemoted": "This user is no longer an administrator",
		"deletedUser": "Successfully deleted user | Successfully deleted user | Successfully deleted users",
		"bannedUser": "Successfully banned user",
		"r10Id": "Kode",
		"updatedR10Id": "Kode ble oppdatert",
		"failedToUpdatedR10Id": "Kunne ikke oppdatere kode"
	},
	"no": {
		"admin": "Administrator",
		"ban": "Utesteng",
		"delete": "Slett bruker | Slett bruker | Slett brukere",
		"editedDate": "Dato endret",
		"email": "E-post adresse",
		"lastLogin": "Siste innlogging",
		"name": "Navn",
		"new": "Ny bruker",
		"update": "Oppdater",
		"usersFound": "Ingen brukere funnet | {count} bruker funnet | {count} brukere funnet",
		"failedToFetch": "Kunne ikke hente brukerdata",
		"failedToUpdateName": "Kunne ikke oppdatere navn",
		"failedToUpdateEmail": "Kunne ikke oppdatere e-post",
		"failedToUpdateSuper": "Kunne ikke oppdatere administrator rettigheter",
		"failedToDelete": "Kunne ikke slette bruker",
		"failedToBan": "Kunne ikke fjerne tilgang",
		"updatedName": "Brukerens navn ble oppdatert",
		"updatedEmail": "Brukerens e-post ble oppdatert",
		"superPromoted": "Brukeren ble oppgradert til administrator",
		"superDemoted": "Fjernet administrator rettigheter",
		"deletedUser": "Brukeren ble slettet | Brukeren ble slettet | Slettet {count} brukere",
		"bannedUser": "Brukeren ble utestengt",
		"r10Id": "Kode",
		"updatedR10Id": "Kode ble oppdatert",
		"failedToUpdatedR10Id": "Kunne ikke oppdatere kode"
	}
}
</i18n>
<template>
	<t-vertical-panel class="user-list">
		<div slot="header" class="user-list-header">
			<t-panel-header size="md" icon="user" type="bp">{{ $tc('usersFound', usersFound) }}</t-panel-header>
		</div>
		<div slot="sub-header" class="user-list-sub-header">
			<t-button
				:disabled="!usersSelected"
				appearance="default"
				width="125px"
				iconBefore="trash"
				@click="removeUser"
			>{{ $tc('delete', rowsSelected) }}</t-button>
			<t-button
				appearance="primary"
				iconBefore="plus"
				@click="toggleNewUserModal(true)"
			>{{ $t('new') }}</t-button>
		</div>
		<div class="user-list-content">
			<t-grid
				v-if="usersFound"
				ref="grid"
				:defaultColumnDefinition="defaultColumnDefinition"
				:columnDefinitions="columnDefinitions"
				:rowHeight="40"
				:items="users"
				:selectableMode="['checkbox']"
				localStorageId="userList"
				selectable
				@selected="onRowsSelected"
			>
				<template #r10Id="{ item }">
					<t-text-input
						:value="mapUserIdToUpperCaseR10Id[item.id] || ''"
						@input="mapUserIdToUpperCaseR10Id[item.id] = $event.toUpperCase()"
						@change="updateR10Id({ id: item.id, key: 'r10Id', value: $event.toUpperCase()})"
					/>
				</template>
				<template #name="{ item }">
					<t-text-input :value="item.name" @change="updateName({ id: item.id, key: 'name', value: $event})"/>
				</template>
				<template #email="{ item }">
					<t-text-input :value="item.email" @change="updateEmail({ id: item.id, key: 'email', value: $event })"/>
				</template>
				<template #super="{ item }">
					<t-checkbox :checked="item.super" @change="updateSuper({ id: item.id, key: 'super', value: $event })"/>
				</template>
			</t-grid>

			<t-no-content-panel v-else icon="user" iconType="bp" />
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
	</t-vertical-panel>
</template>

<script>
import {
	mapState, mapActions, mapMutations
} from './store/users.store.js';
import { mapState as nameSpacedState } from 'vuex';
import moment from 'moment';
import { storageDateFormat } from '@/core';
import TTextInput from '@/components/evergreen/inputs/TTextInput';
import TVerticalPanel from '@/components/evergreen/timpex/layers/TVerticalPanel';
import TButton from '@/components/evergreen/buttons/TButton';
import TGrid from '@/components/evergreen/timpex/grid/TGrid';
import TCheckbox from '@/components/evergreen/inputs/TCheckbox';
import TNoContentPanel from '@/components/evergreen/timpex/layers/TNoContentPanel';
import TOverlay from '@/components/evergreen/timpex/layers/TOverlay';

function mapUserIdToUpperCaseR10Id(users) {

	return users.reduce(
		(acc, { id, r10Id }) => ({ ...acc, [id]: r10Id }),
		{ } );
}

export default {
	name: 'UserList',
	components: { TOverlay, TNoContentPanel, TCheckbox, TGrid, TButton, TVerticalPanel, TTextInput },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
			mapUserIdToUpperCaseR10Id: { },
		};
	},
	computed: {
		...mapState([
			'users',
			'loading'
		]),
		...nameSpacedState('login', ['isSuper']),
		usersFound() {
			return this.users.length;
		},
		singleUserSelected() {
			return this.rowsSelected === 1;
		},
		usersSelected() {
			return this.rowsSelected > 0;
		},
		columnDefinitions() {
			return [
				{
					field: 'r10Id',
					displayName: this.$t('r10Id'),
					width: 80,
					minWidth: 80,
				},
				{
					field: 'name',
					displayName: this.$t('name'),
					minWidth: 100,
				},
				{
					field: 'email',
					displayName: this.$t('email'),
					minWidth: 100,
				},
				{
					field: 'editedDate',
					displayName: this.$t('editedDate'),
					valueGetter: item => this.dateFormatter(item.editedDate),
					width: 200,
					resizable: false,
					type: 'date',
					comparableValueGetter: item => moment(item.editedDate, storageDateFormat).toISOString()
				},
				{
					field: 'lastLogin',
					displayName: this.$t('lastLogin'),
					valueGetter: item => this.xDateFormatter(item.lastLogin),
					width: 200,
					resizable: false,
					type: 'date',
					comparableValueGetter: item => moment(item.lastLogin, 'x').toISOString(),
				},
				{
					field: 'super',
					displayName: this.$t('admin'),
					width: 100,
					filterable: false,
					align: 'center'
				},
			];
		},
	},
	watch: {
		users: function(users, oldUsers) {
			let hasAddedUsers = users.length > oldUsers.length;

			if (hasAddedUsers) {
				this.mapUserIdToUpperCaseR10Id = mapUserIdToUpperCaseR10Id(users);
			}
		}
	},
	async created() {
		if (this.isSuper) {
			try {
				await this.getUsers();

				this.mapUserIdToUpperCaseR10Id = mapUserIdToUpperCaseR10Id(this.users);
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
		} else {
			this.$router.push('/');
		}
	},
	methods: {
		...mapActions([
			'getUsers',
			'updateUser',
			'deleteUsers',
		]),
		...mapMutations(['toggleNewUserModal']),
		dateFormatter(date) {
			return date ? moment(date, storageDateFormat).format('LLL') : '';
		},
		xDateFormatter(date) {
			return date ? moment(date, 'x').format('LLL') : '';
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		async updateR10Id({ id, key, value }) {

			if (value === '') value = undefined;

			try {
				await this.updateUser({ id, key, value });
				this.$notify({ type: 'success', title: this.$t('updatedR10Id') });
			} catch (e) {
				this.$notify({ type: 'error', title: this.$t('failedToUpdatedR10Id') });
			}
		},
		async updateName({ id, key, value }) {
			try {
				await this.updateUser({ id, key, value });
				this.$notify({ type: 'success', title: this.$t('updatedName') });
			} catch (e) {
				this.$notify({ type: 'error', title: this.$t('failedToUpdateName') });
			}
		},
		async updateEmail({ id, key, value }) {
			try {
				await this.updateUser({ id, key, value });
				this.$notify({ type: 'success', title: this.$t('updatedEmail') });
			} catch (e) {
				this.$notify({ type: 'error', title: this.$t('failedToUpdateEmail') });
			}
		},
		async updateSuper({ id, key, value }) {
			try {
				await this.updateUser({ id, key, value });
				let text = '';
				value ? text = this.$t('superPromoted') : text = this.$t('superDemoted');
				this.$notify({ type: 'success', title: text });
			} catch (e) {
				this.$notify({ type: 'error', title: this.$t('failedToUpdateSuper') });
			}
		},
		async removeUser() {
			let selectedUsers = this.$refs.grid.getSelectedItems();
			try {
				await this.deleteUsers(selectedUsers);
				this.$notify({ type: 'success', title: this.$tc('deletedUser', selectedUsers.length) });
			} catch (e) {
				this.$notify({ type: 'error', title: this.$t('failedToDelete') });
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.user-list {
	.user-list-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.user-list-sub-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding: 0px 15px;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.user-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
